import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, {
  formatApiErrorDetail,
  formatRequestError,
  setSessionAccessToken,
  clearSessionAccessToken,
} from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = checking, false = not authed, object = authed
  const [team, setTeam] = useState(null);
  const [roleInTeam, setRoleInTeam] = useState(null);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTeam(data.team);
      setRoleInTeam(data.role_in_team || null);
      return data.user;
    } catch (e) {
      // 401 here is expected when not logged in — only log unexpected errors.
      if (e?.response?.status && e.response.status !== 401) {
        // eslint-disable-next-line no-console
        console.error("auth/me failed:", e);
      }
      setUser(false);
      setTeam(null);
      setRoleInTeam(null);
      return false;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const establishSession = async (data) => {
    if (data?.token) setSessionAccessToken(data.token);
    const u = await refreshMe();
    if (!u) {
      clearSessionAccessToken();
      throw new Error(
        "Could not establish a session after login. Open the app at http://localhost:3000 with the API at http://localhost:8000.",
      );
    }
    return u;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return establishSession(data);
  };

  const loginWithOtp = async (email, otp) => {
    const { data } = await api.post("/auth/verify-otp", { email, otp });
    return establishSession(data);
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("logout failed:", e);
    }
    clearSessionAccessToken();
    setUser(false);
    setTeam(null);
    setRoleInTeam(null);
  };

  return (
    <AuthContext.Provider value={{ user, team, roleInTeam, login, loginWithOtp, signup, logout, refreshMe, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail, formatRequestError };
